// feature-harden.md 6.5: chain-aware, checksum-real address validation
// [ASVS-5, EIP-55, TRC-20]. EVM validation uses ethers' own isAddress
// (already a direct dependency) — it already implements EIP-55 exactly:
// accepts all-lowercase/all-uppercase unconditionally, and only enforces
// the mixed-case checksum when the input actually has mixed case, which
// is the "accept all-lower/all-upper + warn" affordance the spec asks
// for (the "warn" half is a UI-layer decision, not this module's job).
//
// Tron base58check is hand-rolled from ethers' own decodeBase58/sha256
// primitives (both already direct dependencies) rather than importing
// tronweb, which is only a transitive dependency here (pulled in by
// @tetherto/wdk-wallet-tron) — depending on it directly would mean an
// unpinned, un-hoisted API this project doesn't actually declare a
// dependency on. See DECISIONS.md for the alternative considered and
// why this was chosen.
import { decodeBase58, getBytes, isAddress as ethersIsAddress, sha256, toBeArray } from 'ethers';

export function isValidEvmAddress(address) {
  return typeof address === 'string' && ethersIsAddress(address);
}

const BASE58_ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';

// Standard base58check decode: BigInt-decode via ethers' decodeBase58,
// then restore leading zero bytes that a bare BigInt can't represent —
// each leading '1' character in a base58check string encodes one leading
// 0x00 byte (the Bitcoin/Tron convention decodeBase58 itself is agnostic
// to). Returns null for anything that isn't valid base58 at all, rather
// than throwing, so callers can treat "malformed" and "wrong checksum"
// the same way (both mean "not a valid address").
function base58checkDecode(str) {
  if (typeof str !== 'string' || !str.length) return null;
  for (const ch of str) if (!BASE58_ALPHABET.includes(ch)) return null;
  let leadingOnes = 0;
  for (const ch of str) {
    if (ch === '1') leadingOnes++;
    else break;
  }
  let bytes;
  try {
    const big = decodeBase58(str);
    bytes = big === 0n ? new Uint8Array(0) : getBytes(toBeArray(big));
  } catch {
    return null;
  }
  const full = new Uint8Array(leadingOnes + bytes.length);
  full.set(bytes, leadingOnes);
  return full;
}

// Tron mainnet address version byte — every valid Tron base58check
// address decodes to exactly 25 bytes: this 1-byte prefix, 20 bytes of
// address, 4 bytes of checksum (first 4 bytes of double-SHA256 of the
// first 21 bytes).
const TRON_ADDRESS_PREFIX = 0x41;

export function isValidTronAddress(address) {
  const decoded = base58checkDecode(address);
  if (!decoded || decoded.length !== 25) return false;
  if (decoded[0] !== TRON_ADDRESS_PREFIX) return false;
  const payload = decoded.slice(0, 21);
  const checksum = decoded.slice(21, 25);
  const hash1 = getBytes(sha256(payload));
  const hash2 = getBytes(sha256(hash1));
  for (let i = 0; i < 4; i++) {
    if (hash2[i] !== checksum[i]) return false;
  }
  return true;
}

// Chain-aware: an address that's structurally valid for the *wrong*
// chain (a real Tron address typed into an Ethereum-chain recipient, or
// vice versa) must fail here too — not just "is this valid on some
// chain" — so the UI can't be tricked into accepting a cross-chain
// address for a chain it was never meant for. `chain` matches this
// codebase's existing chain-name strings (CONFIG.NET / CONFIG.CHAINS —
// e.g. 'Ethereum · ERC-20', 'Tron · TRC-20'), not a bare chain id.
export function isValidAddressForChain(address, chain) {
  if (typeof chain === 'string' && chain.startsWith('Tron')) return isValidTronAddress(address);
  return isValidEvmAddress(address);
}
